import React from "react";
import { defaultColors, styled } from "../stitches.config";
import { bodyLarge, pStrong, h3, body, h4 } from "./commonStyles/Fonts";
import Avatar from "./atoms/Avatar";
import QuoteIcon from "./assets/icons/quote-icon.svg";
import QuoteDotsIcon from "./assets/icons/quote-dots-icon.svg";
import { BlockquoteElement, ChildArray } from "../components/Article/Parts/Slate/types";
import RenderSlateChildren from "../components/Article/Parts/Slate/RenderSlateChildren";
import { withThemeContext } from "./components/StitchesThemeProvider";

type ExtendedQuoteProps = {
  text?: string;
  author: string | ChildArray;
  authorName?: string;
  time?: string;
  day?: string;
  src?: string;
  url?: string;
};

type QuoteProps = {
  element: BlockquoteElement & ExtendedQuoteProps;
  isDarkMode?: boolean;
};

const Quote: React.FC<QuoteProps> = ({
  element,
  isDarkMode,
  ...props
}) => {
  const isDesktop = window.innerWidth >= 992;
  const {
    author,
    authorAbout,
    children,
    text,
    url,
    src,
    authorName,
    day,
    time,
  } = element;
  return (
    <StyledQuote variant={isDarkMode ? "dark" : undefined}>
      <StyledQuoteIcon>
        <svg viewBox="0 0 88 88" fill="none" xmlns="http://www.w3.org/2000/svg">
          <g clip-path="url(#clip0_4145_15939)">
            <path d="M84.3163 8V13.2814C76.8044 16.012 70.9176 20.3234 66.6561 26.2156C62.3945 32.0359 60.2637 38.1078 60.2637 44.4311C60.2637 45.7964 60.4804 46.8024 60.9138 47.4491C61.2027 47.8802 61.5278 48.0958 61.8889 48.0958C62.2501 48.0958 62.7918 47.8443 63.5141 47.3413C65.8254 45.6886 68.7508 44.8623 72.29 44.8623C76.4071 44.8623 80.0547 46.5509 83.2328 49.9281C86.4109 53.2335 88 57.1856 88 61.7844C88 66.5988 86.1581 70.8383 82.4744 74.503C78.8629 78.1677 74.4569 80 69.2564 80C63.1891 80 57.9524 77.521 53.5464 72.5629C49.1404 67.6048 46.9373 60.9581 46.9373 52.6228C46.9373 42.9222 49.9349 34.2275 55.93 26.5389C61.925 18.8503 71.3871 12.6707 84.3163 8ZM37.3789 8V13.2814C29.867 16.012 23.9803 20.3234 19.7187 26.2156C15.4572 32.0359 13.3264 38.1078 13.3264 44.4311C13.3264 45.7964 13.5431 46.8024 13.9765 47.4491C14.2654 47.8802 14.5904 48.0958 14.9516 48.0958C15.3127 48.0958 15.8544 47.8443 16.5767 47.3413C18.8881 45.6886 21.8134 44.8623 25.3527 44.8623C29.4698 44.8623 33.1174 46.5509 36.2955 49.9281C39.4736 53.2335 41.0627 57.1856 41.0627 61.7844C41.0627 66.5988 39.2208 70.8383 35.5371 74.503C31.9256 78.1677 27.5196 80 22.319 80C16.2517 80 11.015 77.521 6.60903 72.5629C2.20301 67.6048 0 60.9581 0 52.6228C0 42.9222 2.99754 34.2275 8.99261 26.5389C14.9877 18.8503 24.4498 12.6707 37.3789 8Z" />
          </g>
          <defs>
            <clipPath id="clip0_4145_15939">
              <rect width="88" height="88" fill="white" />
            </clipPath>
          </defs>
        </svg>
      </StyledQuoteIcon>
      <div>
        {isDesktop ? (
          <span className={h3()}>
            <RenderSlateChildren element={children} />
          </span>
        ) : (
          <span className={h4()}>
            <RenderSlateChildren element={children} />
          </span>
        )}
        <StyledQuoteAuthor>
          {src && url && <Avatar src={src} url={url} />}
          <div>
            <p className={bodyLarge()}>
              {typeof author === 'string'
                ? author
                : <RenderSlateChildren element={author} />
              }
            </p>
            {authorAbout && (
              <p className={body()}>
                <span>{authorAbout}</span>
              </p>
            )}
            {day && time && (
              <p className={body()}>
                <span>
                  {day}, {time}
                </span>
              </p>
            )}
          </div>
        </StyledQuoteAuthor>
      </div>
      <StyledQuoteDotsIcon>
        <svg viewBox="0 0 48 166" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M95.2473 46.8638C95.348 56.1217 92.652 65.2002 87.5018 72.9415C82.3506 80.6845 74.9773 86.7391 66.3216 90.3313C57.6659 93.9235 48.1212 94.89 38.9059 93.1068C29.6906 91.3237 21.2239 86.8722 14.5857 80.3219C7.9475 73.7716 3.43863 65.4197 1.63272 56.3322C-0.173185 47.2449 0.805391 37.8325 4.44405 29.2956C8.08276 20.7587 14.2172 13.4833 22.0661 8.39895C29.9137 3.31547 39.1189 0.653176 48.5076 0.752691C60.8104 1.05494 72.5228 6.01231 81.222 14.5963C89.9209 23.18 94.9411 34.7328 95.2473 46.8638ZM95.2473 166.135C95.348 175.393 92.652 184.472 87.5018 192.213C82.3506 199.956 74.9773 206.011 66.3216 209.603C57.6659 213.195 48.1212 214.162 38.9059 212.378C29.6906 210.595 21.2239 206.144 14.5857 199.593C7.9475 193.043 3.43863 184.691 1.63272 175.604C-0.173185 166.516 0.805391 157.104 4.44405 148.567C8.08276 140.03 14.2172 132.755 22.0661 127.671C29.9136 122.587 39.1186 119.925 48.5072 120.024C60.8101 120.326 72.5227 125.284 81.222 133.868C89.9209 142.452 94.9411 154.004 95.2473 166.135Z" stroke-width="1.5" />
        </svg>
      </StyledQuoteDotsIcon>
    </StyledQuote>
  );
};

const StyledQuote = styled("div", {
  background: "$grayscale100",
  position: "relative",
  padding: "45px 64px 48px 24px",
  margin: "24px 0 72px 0",
  "@bp4": {
    padding: "72px 152px 72px 64px",
    margin: "32px 0",
  },
  "& span": {
    margin: 0,
    color: defaultColors.grayscale0,
  },
  variants: {
    variant: {
      dark: {
        background: "#232323",
      },
    },
  },
});

const StyledQuoteIcon = styled("div", {
  width: "64px",
  height: "58px",
  margin: "0 0 30px 0",
  "@bp4": {
    width: "88px",
    height: "82px",
    margin: "0 0 40px 0",
  },
  "svg": {
    fill: "$primary",
    width: "64px",
    height: "58px",
    "@bp4": {
      width: "88px",
      height: "82px",
    },
  }
});

const StyledQuoteDotsIcon = styled("div", {
  position: "absolute",
  right: 0,
  bottom: 0,
  width: "40px",
  height: "137px",
  flexShrink: "0",
  "@bp4": {
    width: "80px",
    height: "278px",
  },
  "svg": {
    stroke: "$primary",
    width: "40px",
    height: "137px",
    "@bp4": {
      width: "80px",
      height: "278px",
    },
  }
});

const StyledQuoteAuthor = styled("div", {
  marginTop: "24px",
  display: "flex",
  alignItems: "center",
  color: defaultColors.grayscale0,
  "& .avatar": {
    width: "42px",
    height: "42px",
    marginRight: "12px",
  },
  "& span": {
    color: "$grayscale75",
  },
  "& p": {
    marginTop: "0px",
    marginBottom: "0px",
  },
});

// Design z figmy: UI Components

/* 
const StyledQuote = styled('div', {
    background: "$grayscale100",
    borderLeft: "4px solid $primary",
    position: "relative",
    padding: "37px 64px 40px 40px",
    '@bp4': {
        padding: "40px 64px 40px 40px",
        display: "flex",
    },
  },
});

const StyledQuoteIcon = styled("div", {
  width: "64px",
  height: "58px",
  flexShrink: "0",
  backgroundImage: `url(${QuoteIcon})`,
  backgroundSize: "cover",
  margin: "0 0 30px 0",
  "@bp4": {
    width: "88px",
    height: "82px",
    margin: "0 46px 0 0",
  },
});

const StyledQuoteDotsIcon = styled("div", {
  position: "absolute",
  right: 0,
  bottom: 0,
  width: "40px",
  height: "138px",
  flexShrink: "0",
  backgroundImage: `url(${QuoteDotsIcon})`,
  backgroundSize: "cover",
});

const StyledQuoteAuthor = styled('div', {
    marginTop: "24px",
    display: "flex",
    alignItems: "center",
    color: "$grayscale0",
    "& .avatar": {
        width: "42px",
        height: "42px",
        marginRight: "12px",
    },
    "& span": {
        color: "$grayscale75",
    }
}) */

export default withThemeContext(Quote);
